export interface QuizQuestion {
  question: string
  options: string[]
  correct: number
  explanation: string
}
export interface Quiz {
  id: string
  title: string
  description: string
  ageRange: [number, number]
  category:
    | 'consent'
    | 'relationships'
    | 'safety'
    | 'health'
    | 'emotions'
    | 'online'
  difficulty: 'easy' | 'medium' | 'hard'
  estimatedTime: number
  questions: QuizQuestion[]
}
export const quizzes: Quiz[] = [
  {
    id: 'consent-basics',
    title: 'Consent 101',
    description: 'Master the fundamentals of consent and boundaries',
    ageRange: [13, 24],
    category: 'consent',
    difficulty: 'easy',
    estimatedTime: 5,
    questions: [
      {
        question: 'What does consent mean?',
        options: [
          'Saying yes once means yes forever',
          'Clear, enthusiastic permission that can be withdrawn anytime',
          'Going along with something to avoid conflict',
          'Implied agreement through silence',
        ],
        correct: 1,
        explanation:
          "Consent must be freely given, reversible, informed, enthusiastic, and specific. It's an ongoing conversation!",
      },
      {
        question: 'If someone is drunk or high, can they give consent?',
        options: [
          'Yes, if they say yes',
          'No, they cannot give valid consent',
          'It depends on how drunk they are',
          'Yes, if they initiated it',
        ],
        correct: 1,
        explanation:
          "Someone who is intoxicated cannot give valid consent. They're not in a state to make informed decisions.",
      },
      {
        question: 'Can you change your mind after initially saying yes?',
        options: [
          'No, you must follow through',
          'Yes, consent can be withdrawn at any time',
          'Only if you have a good reason',
          'Only before anything physical happens',
        ],
        correct: 1,
        explanation:
          'Consent is reversible! You can change your mind at ANY point.',
      },
    ],
  },
  {
    id: 'body-positivity-quiz',
    title: 'Body Confidence Challenge',
    description: 'Test your knowledge about body positivity',
    ageRange: [12, 22],
    category: 'health',
    difficulty: 'easy',
    estimatedTime: 5,
    questions: [
      {
        question: 'What is body positivity?',
        options: [
          'Only loving your body when you look a certain way',
          'Accepting and appreciating your body as it is',
          'Ignoring health concerns',
          'Comparing yourself to others positively',
        ],
        correct: 1,
        explanation:
          "Body positivity means accepting your body regardless of size, shape, or appearance. It's about self-acceptance!",
      },
      {
        question: 'How do filters affect body image?',
        options: [
          'They help you look your best',
          'They create unrealistic beauty standards',
          'They have no effect',
          'They only affect insecure people',
        ],
        correct: 1,
        explanation:
          'Filters create unrealistic standards. When we compare our real selves to edited images, it harms self-esteem.',
      },
      {
        question: 'Is it normal for bodies to change during puberty?',
        options: [
          'No, only some people change',
          'Yes, everyone experiences changes at their own pace',
          'Only if you gain weight',
          'No, bodies should stay the same',
        ],
        correct: 1,
        explanation:
          "During puberty, everyone's body changes - height, weight, shape, and more. These changes are completely normal!",
      },
    ],
  },
  {
    id: 'healthy-relationships-quiz',
    title: 'Relationship Red Flags',
    description: 'Learn to identify healthy vs unhealthy relationships',
    ageRange: [14, 24],
    category: 'relationships',
    difficulty: 'medium',
    estimatedTime: 6,
    questions: [
      {
        question: 'What is a healthy relationship?',
        options: [
          "When you're always together",
          "When there's mutual respect, trust, and communication",
          'When you never fight',
          'When one person makes all decisions',
        ],
        correct: 1,
        explanation:
          "Healthy relationships have mutual respect, trust, and honest communication. It's okay to disagree!",
      },
      {
        question: 'Which is a red flag?',
        options: [
          'Having different opinions',
          'Your partner isolating you from friends',
          'Needing alone time',
          'Disagreeing about small things',
        ],
        correct: 1,
        explanation:
          'Isolation from friends is a major red flag! Healthy partners encourage your other relationships.',
      },
      {
        question:
          'What should you do if your partner checks your phone constantly?',
        options: [
          'Give them your password',
          'Recognize it as controlling behavior',
          'Check their phone too',
          'Delete messages',
        ],
        correct: 1,
        explanation:
          'Constantly checking your phone is controlling. Healthy relationships are built on trust!',
      },
    ],
  },
  {
    id: 'emotional-intelligence-quiz',
    title: 'Understanding Your Emotions',
    description: 'Build emotional awareness',
    ageRange: [12, 24],
    category: 'emotions',
    difficulty: 'easy',
    estimatedTime: 5,
    questions: [
      {
        question: 'What is self-esteem?',
        options: [
          'How much others like you',
          'How you value and perceive yourself',
          'How attractive you are',
          'How popular you are',
        ],
        correct: 1,
        explanation:
          "Self-esteem is how you value yourself. It's about your internal sense of worth!",
      },
      {
        question: 'Is it normal to feel sad sometimes?',
        options: [
          'No, you should always be happy',
          'Yes, all emotions are normal and valid',
          'Only if something bad happened',
          'No, it means something is wrong',
        ],
        correct: 1,
        explanation:
          "All emotions are natural and valid. It's okay to not be okay sometimes!",
      },
      {
        question: "What should you do if you're feeling overwhelmed?",
        options: [
          'Keep it to yourself',
          'Talk to someone you trust',
          'Ignore the feelings',
          'Just push through it',
        ],
        correct: 1,
        explanation:
          'Talk to someone! Asking for support is a sign of strength, not weakness.',
      },
    ],
  },
  {
    id: 'communication-skills-quiz',
    title: 'Communication Mastery',
    description: 'Learn effective communication',
    ageRange: [13, 22],
    category: 'relationships',
    difficulty: 'medium',
    estimatedTime: 6,
    questions: [
      {
        question: 'What is active listening?',
        options: [
          'Waiting for your turn to talk',
          'Fully focusing on what someone is saying',
          'Agreeing with everything',
          'Thinking about your response',
        ],
        correct: 1,
        explanation:
          'Active listening means fully concentrating on what someone is saying, not just waiting to speak!',
      },
      {
        question: 'How should you express disagreement?',
        options: [
          'Attack their character',
          'Use "I" statements',
          'Raise your voice',
          'Dismiss their opinion',
        ],
        correct: 1,
        explanation:
          '"I" statements help express disagreement without attacking: "I see it differently..."',
      },
      {
        question: 'What is assertive communication?',
        options: [
          'Being aggressive',
          'Expressing needs while respecting others',
          'Always agreeing',
          'Being passive',
        ],
        correct: 1,
        explanation:
          'Assertive communication means expressing yourself clearly while respecting others!',
      },
    ],
  },
]
export function getQuizzesForAge(age: number): Quiz[] {
  return quizzes.filter(
    (quiz) => age >= quiz.ageRange[0] && age <= quiz.ageRange[1],
  )
}
export function getQuizById(id: string): Quiz | undefined {
  return quizzes.find((quiz) => quiz.id === id)
}
